import { useState } from 'react'
import { ChevronDown, ChevronRight, AlertTriangle, ExternalLink } from 'lucide-react'
import { Button, PageHeader, EmptyState, Spinner, Pagination, cn, formatDateTime } from '@rr/ui'
import {
  useErrorReports,
  useUpdateErrorReportStatus,
  type ErrorReport,
  type ErrorReportStatus,
} from '@/modules/error-reports/useErrorReports'

const STATUSES: { value: ErrorReportStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'resolved', label: 'Resolved' },
]

const STATUS_TONE: Record<ErrorReportStatus, string> = {
  new: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  triaged: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
}

export default function ErrorReportsPage() {
  const [status, setStatus] = useState<ErrorReportStatus | 'all'>('all')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useErrorReports({
    page,
    limit: 20,
    status: status === 'all' ? undefined : status,
  })

  const reports = data?.data.items ?? []
  const pagination = data?.data.pagination

  return (
    <div className="space-y-5">
      <PageHeader
        title="Error reports"
        subtitle="Client-side errors submitted from the apps."
      />

      {/* Status filter */}
      <div className="inline-flex rounded-[10px] border border-border bg-muted/40 p-0.5">
        {([{ value: 'all', label: 'All' }, ...STATUSES] as const).map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => {
              setStatus(s.value as ErrorReportStatus | 'all')
              setPage(1)
            }}
            className={cn(
              'rounded-[8px] px-3.5 py-1.5 text-[13px] font-medium transition-colors',
              status === s.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-[16px] border border-border bg-card p-10">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load reports"
            description="The error-reports endpoint returned an error."
          />
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-[16px] border border-border bg-card p-10">
          <EmptyState
            icon={AlertTriangle}
            title="No error reports"
            description="Nothing has been reported for this filter."
          />
        </div>
      ) : (
        <div className="rounded-[16px] border border-border bg-card shadow-card">
          <ul className="divide-y divide-border">
            {reports.map((r) => (
              <ReportRow key={r.id} report={r} />
            ))}
          </ul>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Pagination
          page={pagination.page}
          pageSize={pagination.limit}
          total={pagination.total}
          onChange={(p) => setPage(p)}
        />
      )}
    </div>
  )
}

function ReportRow({ report }: { report: ErrorReport }) {
  const [open, setOpen] = useState(false)
  const updateStatus = useUpdateErrorReportStatus()

  return (
    <li>
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-0.5 shrink-0 text-muted-foreground"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-[6px] bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {report.app}
            </span>
            {report.statusCode != null && (
              <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                HTTP {report.statusCode}
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">
              {formatDateTime(report.createdAt)}
            </span>
          </div>
          <p className="mt-1 truncate text-[13.5px] font-medium text-foreground">
            {report.message}
          </p>
          <p className="truncate text-[11.5px] text-muted-foreground">{report.url}</p>
        </div>

        {/* Status — click to cycle / set */}
        <div className="flex shrink-0 items-center gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              disabled={updateStatus.isPending}
              onClick={() => updateStatus.mutate({ id: report.id, status: s.value })}
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold transition',
                report.status === s.value
                  ? STATUS_TONE[s.value]
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {open && (
        <div className="space-y-3 border-t border-border bg-muted/20 px-4 py-3 pl-11">
          <Meta label="URL">
            <a
              href={report.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-info hover:underline"
            >
              {report.url} <ExternalLink className="h-3 w-3" />
            </a>
          </Meta>
          <Meta label="User agent">{report.userAgent || '—'}</Meta>
          <Meta label="App version">{report.appVersion || '—'}</Meta>
          <Meta label="Company / User">
            {report.companyId ?? '—'} / {report.userId ?? '—'}
          </Meta>
          {report.stack && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Stack
              </p>
              <pre className="max-h-64 overflow-auto rounded-[8px] border border-border bg-background p-3 text-[11px] leading-relaxed text-foreground">
                {report.stack}
              </pre>
            </div>
          )}
          {report.context != null && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Context
              </p>
              <pre className="max-h-48 overflow-auto rounded-[8px] border border-border bg-background p-3 text-[11px] text-foreground">
                {JSON.stringify(report.context, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2 text-[12px]">
      <span className="shrink-0 font-medium text-muted-foreground">{label}:</span>
      <span className="min-w-0 break-all text-foreground">{children}</span>
    </div>
  )
}
